import {
  IActivityDetailResponse,
  IActivityForm,
  IActivityListFilters,
  IActivityListResponse,
  IActivityDriverForm,
  IActivityDriverListResponse,
  IActivityPlanForm,
  IActivityPlanListResponse,
  IActivityOutputForm,
  IActivityOutputListResponse,
  IActivityAnalyticsByActivityItem,
  IActivityAnalyticsByTypeItem,
  IActivityAnalyticsFilters,
  IActivityAnalyticsSummary,
} from './activity.models';

import { ILookupItem } from '../forms/forms.models';

interface IApiEnvelope<T> {
  ok: boolean;
  message?: string;
  data: T;
  errors?: Array<{
    field?: string;
    code?: string;
    message: string;
  }>;
  warnings?: Array<{
    field?: string;
    code?: string;
    message: string;
  }>;
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const searchParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && `${value}`.trim() !== '') {
      searchParams.append(key, `${value}`);
    }
  });

  const query = searchParams.toString();
  return query ? `?${query}` : '';
}

async function getJson<T>(url: string): Promise<T> {
  const response = await fetch(url, {
    method: 'GET',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

async function sendJson<T>(
  url: string,
  method: 'POST' | 'PUT',
  body: any
): Promise<T> {
  const response = await fetch(url, {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  return (await response.json()) as T;
}

export async function fetchActivitiesList(
  filters: IActivityListFilters
): Promise<IActivityListResponse> {
  const query = buildQuery({
    search: filters.search,
    typeix: filters.typeix,
    costcenterix: filters.costcenterix,
    status: filters.status,
    page: filters.page,
    pagesize: filters.pagesize,
  });

  const payload = await getJson<IApiEnvelope<IActivityListResponse>>(
    `/api/activity/activities${query}`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activities');
  }

  return payload.data;
}

export async function fetchActivityTypes(): Promise<ILookupItem[]> {
  const payload = await getJson<IApiEnvelope<{ items: ILookupItem[] }>>(
    `/api/activity/activitytypes`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity types');
  }

  return payload.data.items || [];
}

export async function fetchActivityParents(): Promise<ILookupItem[]> {
  const payload = await getJson<IApiEnvelope<{ items: ILookupItem[] }>>(
    `/api/activity/activities/lookup`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load parent activities');
  }

  return payload.data.items || [];
}

export async function fetchActivityDetail(
  activityix: string
): Promise<IActivityDetailResponse> {
  const payload = await getJson<IApiEnvelope<IActivityDetailResponse>>(
    `/api/activity/activities/${activityix}`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity detail');
  }

  return payload.data;
}

export async function createActivity(payloadBody: IActivityForm): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activities`,
    'POST',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to create activity');
  }

  return payload.data;
}

export async function updateActivity(
  activityix: string,
  payloadBody: IActivityForm
): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activities/${activityix}`,
    'PUT',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to update activity');
  }

  return payload.data;
}


export async function fetchDrivers(): Promise<ILookupItem[]> {
  const payload = await getJson<IApiEnvelope<{ items: ILookupItem[] }>>(
    `/api/activity/drivers`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load drivers');
  }

  return payload.data.items || [];
}

export async function fetchActivityDriverMappings(
  activityix: string
): Promise<IActivityDriverListResponse> {
  const payload = await getJson<IApiEnvelope<IActivityDriverListResponse>>(
    `/api/activity/activities/${activityix}/drivers`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity drivers');
  }

  return payload.data;
}

export async function createActivityDriverMapping(
  payloadBody: IActivityDriverForm
): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activitydrivers`,
    'POST',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to create activity driver mapping');
  }

  return payload.data;
}

export async function updateActivityDriverMapping(
  recix: string,
  payloadBody: IActivityDriverForm
): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activitydrivers/${recix}`,
    'PUT',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to update activity driver mapping');
  }

  return payload.data;
}

export async function disableActivityDriverMapping(recix: string): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activitydrivers/${recix}/disable`,
    'POST',
    {}
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to disable activity driver mapping');
  }

  return payload.data;
}

export async function fetchActivityPlans(
  activityix: string
): Promise<IActivityPlanListResponse> {
  const payload = await getJson<IApiEnvelope<IActivityPlanListResponse>>(
    `/api/activity/activities/${activityix}/plans`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity plans');
  }

  return payload.data;
}

export async function createActivityPlan(
  payloadBody: IActivityPlanForm
): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activityplans`,
    'POST',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to create activity plan');
  }

  return payload.data;
}

export async function updateActivityPlan(
  recix: string,
  payloadBody: IActivityPlanForm
): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activityplans/${recix}`,
    'PUT',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to update activity plan');
  }

  return payload.data;
}

export async function fetchActivityOutputs(
  activityix: string
): Promise<IActivityOutputListResponse> {
  const payload = await getJson<IApiEnvelope<IActivityOutputListResponse>>(
    `/api/activity/activities/${activityix}/outputs`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity outputs');
  }

  return payload.data;
}

export async function createActivityOutput(
  payloadBody: IActivityOutputForm
): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activityoutputs`,
    'POST',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to create activity output');
  }

  return payload.data;
}

export async function updateActivityOutput(
  recix: string,
  payloadBody: IActivityOutputForm
): Promise<any> {
  const payload = await sendJson<IApiEnvelope<any>>(
    `/api/activity/activityoutputs/${recix}`,
    'PUT',
    payloadBody
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to update activity output');
  }

  return payload.data;
}

export async function fetchActivityAnalyticsSummary(
  filters: IActivityAnalyticsFilters
): Promise<IActivityAnalyticsSummary> {
  const query = buildQuery({
    typeix: filters.typeix,
    costcenterix: filters.costcenterix,
    status: filters.status,
  });

  const payload = await getJson<IApiEnvelope<IActivityAnalyticsSummary>>(
    `/api/activity/analytics/summary${query}`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity analytics summary');
  }

  return payload.data;
}

export async function fetchActivityAnalyticsByType(
  filters: IActivityAnalyticsFilters
): Promise<IActivityAnalyticsByTypeItem[]> {
  const query = buildQuery({
    typeix: filters.typeix,
    costcenterix: filters.costcenterix,
    status: filters.status,
  });

  const payload = await getJson<IApiEnvelope<{ items: IActivityAnalyticsByTypeItem[] }>>(
    `/api/activity/analytics/by-type${query}`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity analytics by type');
  }

  return payload.data.items || [];
}

export async function fetchActivityAnalyticsByActivity(
  filters: IActivityAnalyticsFilters
): Promise<IActivityAnalyticsByActivityItem[]> {
  const query = buildQuery({
    typeix: filters.typeix,
    costcenterix: filters.costcenterix,
    status: filters.status,
  });

  const payload = await getJson<IApiEnvelope<{ items: IActivityAnalyticsByActivityItem[] }>>(
    `/api/activity/analytics/by-activity${query}`
  );

  if (!payload.ok) {
    throw new Error(payload.message || 'Failed to load activity analytics by activity');
  }

  return payload.data.items || [];
}
